type WarnFn = (message?: any, ...optionalParams: any[]) => void;

/** Builds Auth0 appState return path payload from pathname/search. */
function buildAuth0ReturnTo(pathname: string, search: string): string {
  return `${pathname}${search}`;
}

/** Builds Auth0 appState object used during loginWithRedirect. */
export function buildAuth0AppState(pathname: string, search: string): { returnTo: string } {
  return { returnTo: buildAuth0ReturnTo(pathname, search) };
}

type RestoreAuth0CallbackPathParams = {
  appState?: { returnTo?: string };
  pathname: string;
  replaceState: (url: string) => void;
};

/** Restores callback path after Auth0 redirect. */
export function restoreAuth0CallbackPath({ appState, pathname, replaceState }: RestoreAuth0CallbackPathParams): void {
  const returnTo = appState?.returnTo;

  if (returnTo && returnTo.startsWith("/")) {
    replaceState(returnTo);
    return;
  }

  replaceState(pathname);
}

function createLoginRedirectUrl(loginBaseUrl: string, currentUrl: string): string {
  const separator = loginBaseUrl.includes("?") ? "&" : "?";
  return `${loginBaseUrl}${separator}redirectTo=${encodeURIComponent(currentUrl)}`;
}

type RedirectToLoginParams = {
  loginBaseUrl: string;
  currentUrl?: string;
  navigate?: (url: string) => void;
};

/** Navigates to login URL with encoded redirectTo payload. */
export function redirectToLogin({
  loginBaseUrl,
  currentUrl = window.location.href,
  navigate,
}: RedirectToLoginParams): void {
  const loginRedirectUrl = createLoginRedirectUrl(loginBaseUrl, currentUrl);
  const navigateTo =
    navigate ||
    (url => {
      window.location.href = url;
    });
  navigateTo(loginRedirectUrl);
}

type RedirectToCallingAppIfPresentParams = {
  search?: string;
  navigate?: (to: string) => void;
  warn?: WarnFn;
};

/**
 * Auth0 onRedirectCallback that restores the pre-login path/query using
 * window.history.replaceState. Pass directly to SharedAuth0Provider.
 */
export function onAuth0Redirect(appState?: { returnTo?: string }): void {
  restoreAuth0CallbackPath({
    appState,
    pathname: window.location.pathname,
    replaceState: url => {
      window.history.replaceState({}, document.title, url);
      window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
    },
  });
}

/** Validates redirectTo host and redirects to calling app when allowed. */
export function redirectToCallingAppIfPresent({
  search = window.location.search,
  navigate = to => {
    window.location.href = to;
  },
  warn,
}: RedirectToCallingAppIfPresentParams = {}): boolean {
  const searchParams = new URLSearchParams(search);
  const redirectTo = searchParams.get("redirectTo");
  if (!redirectTo) return false;

  const logWarn = warn || console.warn;

  try {
    const redirectUrl = new URL(redirectTo);
    // Security invariant: never allow redirect target transport downgrade,
    // even for trusted hosts, to avoid leaking auth context over plaintext.
    const isAllowedProtocol = redirectUrl.protocol === "https:";
    const hostSuffix = process.env.NODE_ENV === "production" ? "remerge.io" : "remerge.test";
    const isAllowedHost = redirectUrl.hostname === hostSuffix || redirectUrl.hostname.endsWith(`.${hostSuffix}`);

    if (!isAllowedProtocol) {
      logWarn("The protocol of the url passed to redirectTo must be https");
      return false;
    }

    if (!isAllowedHost) {
      logWarn("The hostname of the url passed to redirectTo must be a subdomain of remerge.io or remerge.test");
      return false;
    }

    navigate(redirectTo);
    return true;
  } catch (error) {
    logWarn("Error parsing redirectTo url", error);
    return false;
  }
}
